import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, agentsApi } from "@/lib/api";
import { formatDakarTime, parseUTC } from "@/lib/time";
import { useSiren } from "@/hooks/useSiren";
import MapView from "@/components/MapView";
import AlertList from "@/components/AlertList";
import AlertDetail from "@/components/AlertDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldAlert, LogOut, Volume2, VolumeX, RefreshCw, Siren, Radio,
  AlertTriangle, UserCheck, CheckCircle2, X, Search, Plus, Loader2, BookOpen, BellOff, Users, ClipboardList, Contact, UserCog, Receipt, ScrollText, Settings,
} from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 5000;

// An alert is considered finished once the client cancels it or it is resolved.
const isEnded = (a) => {
  const s = (a?.status || "").toLowerCase();
  return s === "cancelled" || s === "canceled" || s === "resolved" || s === "ended" || !!a?.ended_at;
};

const SENEGAL_CITIES = [
  { name: "Dakar-Plateau", lat: 14.6708, lng: -17.4381 },
  { name: "Pikine", lat: 14.7549, lng: -17.3906 },
  { name: "Thiès", lat: 14.7910, lng: -16.9256 },
  { name: "Saint-Louis", lat: 16.0326, lng: -16.4818 },
  { name: "Touba", lat: 14.8667, lng: -15.8833 },
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { enabled, active, toggle, start, stop } = useSiren();
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);
  const [handledIds, setHandledIds] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [banner, setBanner] = useState(null);
  const [newIds, setNewIds] = useState(new Set());
  const [simulating, setSimulating] = useState(false);
  const [agentTrack, setAgentTrack] = useState(null);

  const knownRef = useRef(null);
  const alertsRef = useRef([]);
  alertsRef.current = alerts;

  const fetchAlerts = useCallback(async (silent) => {
    try {
      const data = await api.activeAlerts();
      const raw = (data.alerts || []).filter((a) => !handledIds.has(a.alert_id));
      // The active feed may briefly still list an alert the client just ended/cancelled.
      const incoming = raw.filter((a) => !isEnded(a));
      const endedIncoming = raw.filter((a) => isEnded(a));
      const incomingIds = new Set(incoming.map((a) => a.alert_id));
      const incomingMap = new Map(incoming.map((a) => [a.alert_id, a]));
      const prev = alertsRef.current;

      // Alerts we were tracking that are momentarily absent from the active feed
      // (phone backgrounded / screen locked / transient network). We keep showing
      // their last known position UNTIL the backend confirms they really ended or
      // the client cancelled — the position must stay until marked as handled.
      const absent = prev.filter((a) => !incomingIds.has(a.alert_id) && !handledIds.has(a.alert_id));
      const removedIds = new Set();
      const cancelled = [];
      endedIncoming.forEach((a) => {
        removedIds.add(a.alert_id);
        if ((a.status || "").toLowerCase().startsWith("cancel")) cancelled.push(a);
      });
      await Promise.all(
        absent.map(async (a) => {
          try {
            const detail = await api.getAlert(a.alert_id);
            if (detail && (detail.status === "cancelled" || detail.status === "resolved" || detail.ended_at)) {
              removedIds.add(a.alert_id);
              if (detail.status === "cancelled") cancelled.push(detail);
            }
          } catch { /* transient error -> keep retaining the alert */ }
        })
      );

      // Merge: retain previously-tracked alerts, refresh them with live data when
      // present, and keep the last known GPS when a fresh ping is missing.
      const merged = [];
      const seen = new Set();
      for (const old of prev) {
        if (handledIds.has(old.alert_id) || removedIds.has(old.alert_id)) continue;
        const fresh = incomingMap.get(old.alert_id);
        if (fresh) {
          merged.push({
            ...fresh,
            current_location: fresh.current_location || old.current_location,
            locations: fresh.locations && fresh.locations.length ? fresh.locations : (old.locations || []),
          });
        } else {
          merged.push(old);
        }
        seen.add(old.alert_id);
      }
      for (const a of incoming) {
        if (!seen.has(a.alert_id)) { merged.push(a); seen.add(a.alert_id); }
      }
      setAlerts(merged);
      setLastSync(new Date());

      // New-alert detection (siren + banner + auto-focus). Track the full set of
      // alerts we are currently following so a brief reappearance never re-fires.
      if (knownRef.current === null) {
        knownRef.current = new Set(merged.map((a) => a.alert_id));
      } else {
        const prevKnown = knownRef.current;
        const fresh = [...incomingIds].filter((id) => !prevKnown.has(id));
        if (fresh.length > 0) {
          start();
          const freshSet = new Set(fresh);
          setNewIds(freshSet);
          const freshAlerts = incoming.filter((a) => freshSet.has(a.alert_id));
          const names = freshAlerts.map((a) => a.user_name);
          setBanner(`${fresh.length} nouvelle(s) alerte(s) SOS — ${names.join(", ")}`);
          const newest = freshAlerts
            .slice()
            .sort((a, b) => (parseUTC(b.triggered_at)?.getTime() || 0) - (parseUTC(a.triggered_at)?.getTime() || 0))[0];
          if (newest) setSelectedId(newest.alert_id);
        }
        if (cancelled.length > 0) {
          stop();
          setBanner(null);
          setNewIds(new Set());
          cancelled.forEach((detail) => {
            setSelectedId((cur) => (cur === detail.alert_id ? null : cur));
            const when = detail.ended_at ? ` à ${formatDakarTime(detail.ended_at)} (Dakar)` : "";
            toast.info(`Alerte annulée par ${detail.user_name || "le client"}${when}`, { duration: 9000 });
          });
        }
        knownRef.current = new Set(merged.map((a) => a.alert_id));
      }
    } catch (e) {
      if (!silent) toast.error("Erreur de connexion au backend SOS");
    } finally {
      setLoading(false);
    }
  }, [handledIds, start, stop]);

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(() => fetchAlerts(true), POLL_MS);
    return () => { clearInterval(id); };
  }, [fetchAlerts]);

  // Fail-safe: the siren must never outlive the alerts. The moment no active
  // alert remains (client cancelled it or everything was resolved), silence it
  // automatically — no manual action from the supervisor required.
  useEffect(() => {
    if (alerts.length === 0) stop();
  }, [alerts, stop]);

  const selectedAlert = useMemo(
    () => alerts.find((a) => a.alert_id === selectedId) || null,
    [alerts, selectedId]
  );

  const stats = useMemo(() => {
    const active = alerts.filter((a) => !a.assigned_to).length;
    const inProgress = alerts.filter((a) => a.assigned_to).length;
    return { active, inProgress, handled: handledIds.size, total: alerts.length };
  }, [alerts, handledIds]);

  const filtered = useMemo(() => {
    let list = alerts;
    if (filter === "active") list = list.filter((a) => !a.assigned_to);
    if (filter === "progress") list = list.filter((a) => a.assigned_to);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((a) => a.user_name?.toLowerCase().includes(q) || a.user_phone?.toLowerCase().includes(q));
    }
    return list;
  }, [alerts, filter, search]);

  const handleSelect = (a) => { setSelectedId(a.alert_id); };

  // Real-time agent GPS tracking for the selected alert (polls + advances the simulated approach)
  useEffect(() => {
    if (!selectedId) { setAgentTrack(null); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const agent = await agentsApi.byAlert(selectedId);
        if (cancelled) return;
        if (agent && agent.assignment) {
          let updated = agent;
          if (!agent.assignment.arrived && agent.tracking_mode !== "live") {
            const victim = alertsRef.current.find((a) => a.alert_id === selectedId)?.current_location;
            const target = victim ? { target_lat: victim.lat, target_lng: victim.lng } : undefined;
            updated = await agentsApi.advance(agent.id, target);
            if (cancelled) return;
          }
          setAgentTrack({
            id: updated.id,
            name: `${updated.first_name} ${updated.last_name}`,
            points: (updated.track || []).map((p) => [p.lat, p.lng]),
            current: updated.current_location ? [updated.current_location.lat, updated.current_location.lng] : null,
            arrived: !!updated.assignment?.arrived,
          });
        } else {
          setAgentTrack(null);
        }
      } catch { /* ignore transient errors */ }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [selectedId]);

  const handleHandled = async (id) => {
    stop();
    setBanner(null);
    setNewIds(new Set());
    try {
      const ag = await agentsApi.byAlert(id);
      if (ag) await agentsApi.release(ag.id);
    } catch { /* keep closing even if release fails */ }
    setAgentTrack(null);
    setHandledIds((prev) => new Set(prev).add(id));
    setAlerts((prev) => prev.filter((a) => a.alert_id !== id));
    if (knownRef.current) knownRef.current.delete(id);
    setSelectedId(null);
  };

  const simulate = async () => {
    setSimulating(true);
    try {
      const city = SENEGAL_CITIES[Math.floor(Math.random() * SENEGAL_CITIES.length)];
      const jit = () => (Math.random() - 0.5) * 0.01;
      const created = await api.createAlert(city.lat + jit(), city.lng + jit(), 8 + Math.floor(Math.random() * 15));
      // build a small green route (parallel updates)
      await Promise.all(
        [1, 2, 3, 4].map((i) =>
          api.updateLocation(created.alert_id, city.lat + jit() + i * 0.0015, city.lng + jit() + i * 0.0012, 8)
        )
      );
      toast.success(`Alerte simulée depuis ${city.name}`);
      fetchAlerts(true);
    } catch {
      toast.error("Échec de la simulation");
    } finally {
      setSimulating(false);
    }
  };

  const acknowledge = () => { stop(); setBanner(null); setNewIds(new Set()); };

  return (
    <div className="h-screen flex flex-col bg-[#0B1120] overflow-hidden">
      {/* Header */}
      <header className="h-16 shrink-0 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between z-40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold text-slate-100 leading-tight flex items-center gap-2">
              Sama Jaamu
              <span className="hidden sm:inline text-[10px] font-mono font-normal uppercase tracking-wider text-emerald-400 bg-emerald-950 border border-emerald-500/30 px-2 py-0.5 rounded-full">Superviseur</span>
            </h1>
            <p className="text-[11px] font-mono text-slate-500 flex items-center gap-1.5">
              <Radio className={`w-3 h-3 ${loading ? "text-slate-500" : "text-emerald-400 animate-pulse"}`} />
              {lastSync ? `Synchronisé ${formatDakarTime(lastSync)} (Dakar)` : "Connexion..."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <>
              <Button data-testid="clients-link-btn" onClick={() => navigate("/clients")} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 hidden sm:flex">
                <Contact className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden md:inline">Clients</span>
              </Button>
              <Button data-testid="history-link-btn" onClick={() => navigate("/historique")} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 hidden sm:flex">
                <ClipboardList className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden md:inline">Historique</span>
              </Button>
              <Button data-testid="finance-link-btn" onClick={() => navigate("/finances")} size="sm" variant="outline" className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-950/30 hidden sm:flex">
                <Receipt className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden md:inline">Finances</span>
              </Button>
              <Button data-testid="activity-link-btn" onClick={() => navigate("/activite")} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 hidden sm:flex">
                <ScrollText className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden md:inline">Journal</span>
              </Button>
              <Button data-testid="admin-link-btn" onClick={() => navigate("/admin")} size="sm" variant="outline" className="border-yellow-700/50 text-yellow-300 hover:bg-yellow-950/30 hidden sm:flex">
                <UserCog className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden md:inline">Admin</span>
              </Button>
            </>
          )}
          <Button data-testid="team-link-btn" onClick={() => navigate("/equipe")} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 hidden sm:flex">
            <Users className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden md:inline">Équipe</span>
          </Button>
          <Button data-testid="guide-link-btn" onClick={() => navigate("/guide")} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 hidden sm:flex">
            <BookOpen className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden md:inline">Guide</span>
          </Button>
          <Button data-testid="simulate-sos-btn" onClick={simulate} disabled={simulating} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 hidden sm:flex">
            {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 sm:mr-1.5" />}
            <span className="hidden md:inline">Simuler une alerte</span>
          </Button>
          <Button data-testid="refresh-btn" onClick={() => fetchAlerts()} size="icon" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button data-testid="sound-toggle-btn" onClick={toggle} size="icon" variant="outline" className={`border-slate-700 hover:bg-slate-800 ${enabled ? "text-emerald-400" : "text-slate-500"}`} title={enabled ? "Son activé" : "Son coupé"}>
            {enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
          <div className="hidden sm:flex flex-col items-end mr-1 ml-2">
            <span className="text-sm text-slate-200 font-medium leading-tight">{user?.name}</span>
            <span className="text-[10px] font-mono text-slate-500">{user?.email}</span>
          </div>
          <Button data-testid="account-link-btn" onClick={() => navigate("/compte")} size="icon" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" title="Mon compte">
            <Settings className="w-4 h-4" />
          </Button>
          <Button data-testid="logout-btn" onClick={logout} size="icon" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-950/40">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Flashing banner — stays until the supervisor acknowledges or resolves */}
      {banner && (
        <div data-testid="flashing-alert-banner" className="animate-flash text-white font-semibold py-2.5 px-4 flex items-center justify-between gap-3 shadow-lg shrink-0 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <Siren className="w-5 h-5 animate-pulse shrink-0" />
            <span className="text-sm truncate">{banner}</span>
            {active && (
              <span data-testid="siren-active-indicator" className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-mono bg-white/15 px-2 py-0.5 rounded-full">
                <Volume2 className="w-3 h-3 animate-pulse" /> Sirène en cours
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid="acknowledge-btn" onClick={acknowledge} size="sm" className="bg-white text-red-700 hover:bg-red-50 font-semibold h-8">
              <BellOff className="w-4 h-4 mr-1.5" /> Accuser réception
            </Button>
            <button onClick={acknowledge} data-testid="dismiss-banner-btn" className="hover:bg-white/20 rounded-md p-1"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div data-testid="top-stats-bar" className="h-14 shrink-0 bg-slate-900/60 border-b border-slate-800/80 px-4 flex items-center gap-3 sm:gap-6 overflow-x-auto">
        <Stat testid="active-sos-count" icon={AlertTriangle} label="Alertes actives" value={stats.active} color="text-red-400" />
        <div className="w-px h-6 bg-slate-800" />
        <Stat testid="in-progress-count" icon={UserCheck} label="En cours" value={stats.inProgress} color="text-amber-400" />
        <div className="w-px h-6 bg-slate-800" />
        <Stat testid="handled-count" icon={CheckCircle2} label="Traitées" value={stats.handled} color="text-emerald-400" />
      </div>

      {/* Main split */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <aside className="w-full lg:w-[420px] bg-slate-950/90 border-r border-slate-800 flex flex-col shrink-0 h-[45vh] lg:h-auto overflow-hidden">
          {selectedAlert ? (
            <AlertDetail
              alert={selectedAlert}
              onBack={() => setSelectedId(null)}
              onUpdated={() => fetchAlerts(true)}
              onHandled={handleHandled}
            />
          ) : (
            <>
              <div className="p-4 border-b border-slate-800 space-y-3 shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    data-testid="search-alerts-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher par nom ou téléphone..."
                    className="pl-9 bg-slate-900 border-slate-700 text-slate-100 h-10"
                  />
                </div>
                <div className="flex gap-1.5">
                  {[
                    { k: "all", label: "Toutes", tid: "filter-status-all" },
                    { k: "active", label: "Actives", tid: "filter-status-active" },
                    { k: "progress", label: "En cours", tid: "filter-status-progress" },
                  ].map((f) => (
                    <button
                      key={f.k}
                      data-testid={f.tid}
                      onClick={() => setFilter(f.k)}
                      className={`flex-1 text-xs font-medium py-2 rounded-lg border transition-all ${
                        filter === f.k
                          ? "bg-emerald-600 border-emerald-500 text-white"
                          : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading && alerts.length === 0 ? (
                  <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : (
                  <AlertList alerts={filtered} selectedId={selectedId} onSelect={handleSelect} newIds={newIds} />
                )}
              </div>
            </>
          )}
        </aside>

        <main className="flex-1 relative bg-slate-950 h-[55vh] lg:h-auto">
          <MapView alerts={alerts} selectedAlert={selectedAlert} onSelect={handleSelect} agentTrack={agentTrack} />
          <div data-testid="map-legend" className="absolute bottom-4 left-4 z-[500] bg-slate-900/90 backdrop-blur border border-slate-800 rounded-lg px-3 py-2 flex items-center gap-3 text-[11px] font-mono text-slate-400 pointer-events-none">
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 rounded-full bg-red-500" /> Client</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 rounded-full bg-blue-500" /> Agent</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /> Assignée</span>
          </div>
        </main>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="flex items-center gap-2.5 shrink-0" data-testid={testid}>
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <p className={`font-display font-bold text-xl leading-none ${color}`}>{value}</p>
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  </div>
);

export default Dashboard;
